<template>
  <div>
    <div class="distance_subtitle">
      Поле {{ fieldIndex + 1 }}
      <div
        :id="`button-delete-additional-field-tooltip-${fieldIndex + 1}`"
        class="d-inline-block"
        tabindex="0"
      >
        <b-button
          title="Удалить"
          variant="flat-danger"
          class="btn-icon inline-block"
          size="sm"
          :disabled="isRestricted"
          @click="deleteField"
        >
          <feather-icon icon="MinusIcon" />
        </b-button>
      </div>
      <b-tooltip
        v-if="isRestricted"
        :target="`button-delete-additional-field-tooltip-${fieldIndex + 1}`"
        triggers="hover"
      >
        В данной дистанции есть регистрации
      </b-tooltip>
    </div>

    <template v-if="fieldState">
      <b-form-checkbox
        v-model="fieldState.is_optional"
        default="false"
        class="distance-add--checkbox"
      >
        <span class="distance_subtitle">Сделать поле необязательным?</span>
      </b-form-checkbox>

      <b-form-checkbox
        v-model="fieldState.is_public"
        default="false"
        class="distance-add--checkbox"
      ><span class="distance_subtitle">Показывать ответы в результатах?</span>
      </b-form-checkbox>

      <div
        :key="'field.name' + fieldIndex"
        class="distance-add--form"
      >
        <label
          for="addName"
          class="distance_label"
        >Название поля</label>

        <b-form-input
          id="addName"
          :key="'field.name_' + fieldIndex"
          v-model="fieldState.name"
          placeholder="Название поля"
          class="inline-block"
        />
      </div>

      <div
        v-if="options.length > 0"
        :key="'field.parent' + fieldIndex"
        class="distance-add--form"
      >
        <label class="distance_subtitle"> Зависимость от:</label>
        <v-select
          v-model="fieldState.parent_uuid"
          placeholder="Выберите вопрос"
          :reduce="option => option.uuid"
          :options="options"
          label="name"
          @input="changeParent"
        />

        <div
          v-if="fieldState.parent && fieldState.parent.type === 'Синглселект'"
          class="distance-add--form"
        >
          <label class="distance_subtitle"> Выберите значение</label>
          <v-select
            v-model="fieldState.expected_answer_uuid"
            :reduce="option => option.uuid"
            label="title"
            :options="fieldState.parent.options"
          />
        </div>
      </div>

      <div
        :key="'field.type' + fieldIndex"
        class="distance-add--form"
      >
        <label class="distance_subtitle">Тип поля</label>
        <v-select
          v-model="fieldState.type"
          label="value"
          :options="fieldType"
          placeholder="Тип поля"
          :reduce="option => option.value"
        />
      </div>

      <div class="distance-add--form">
        <label class="distance_subtitle">Дополнительное описание</label>
        <b-form-input
          v-model="fieldState.description"
          placeholder="Дополнительное описание"
        />
      </div>

      <div class="distance-add--form">
        <label class="distance_subtitle">Срок действия поля</label>
        <p class="distance-add--hint">
          Дата начала включается в срок действия, дата окончания — нет.
        </p>
        <label class="distance_label">Дата начала (включительно)</label>
        <flat-pickr
          v-model="fieldState.date_from"
          placeholder="Дата начала"
          class="form-control"
          :config="{
            ...dateConfig,
            defaultHour: 0,
          }"
        />
        <label class="distance_label mt-50">Дата окончания (не включительно)</label>
        <flat-pickr
          v-model="fieldState.date_to"
          placeholder="Дата окончания"
          class="form-control"
          :config="{
            ...dateConfig,
            defaultHour: 23,
            defaultMinute: 59,
          }"
        />
      </div>

      <div class="distance-add--form">
        <label class="distance_subtitle">Изображения</label>
        <file-multi-uploader
          :file-list="fieldState.images"
          @input="ids => $set(fieldState, 'image_ids', ids)"
          @files-change="files => $set(fieldState, 'images', files)"
        />
      </div>

      <div
        v-if="
          fieldState.options &&
            (fieldState.type === 'Синглселект' ||
              fieldState.type === 'Мультиселект')
        "
        class="distance-add-options"
      >
        <p>Добавленные опции</p>
        <div
          v-for="(option, index) in fieldState.options"
          :key="option.uuid || index"
          class="added-option"
        >
          <b-button
            title="Удалить"
            variant="flat-danger"
            class="btn-icon inline-block flex-shrink-0"
            size="sm"
            @click="() => removeSingleOption(index)"
          >
            <feather-icon icon="MinusIcon" />
          </b-button>

          <div
            v-if="editedOptionIndex === index"
            class="option-content option-content--editing"
          >
            <div class="option-edit-fields">
              <b-form-input
                v-model="editedOption.title"
                type="text"
                placeholder="Название"
              />
              <price-input-wrapper
                :value="editedOption.price"
                @input="value => (editedOption.price = value)"
              />
              <b-form-input
                v-model="editedOption.description"
                type="text"
                placeholder="Описание"
                class="mt-50"
              />
              <div class="mt-50">
                <label class="distance_subtitle">Изображения</label>
                <file-multi-uploader
                  :file-list="editedOption.images || []"
                  @input="ids => (editedOption.image_ids = ids)"
                  @files-change="files => (editedOption.images = files)"
                />
              </div>
            </div>
            <b-button
              title="Сохранить"
              variant="primary"
              class="btn-icon flex-shrink-0 align-self-start"
              size="sm"
              @click="saveEditedOption"
            >
              <feather-icon icon="Edit2Icon" />
            </b-button>
          </div>

          <div
            v-else
            class="option-content"
          >
            <div class="option-info">
              <p>
                {{ option.title }}
                <span v-if="option.price">
                  - {{ option.price / 100 }} рублей</span>
              </p>
              <p
                v-if="option.description"
                class="option-description"
              >
                {{ option.description }}
              </p>
            </div>
            <feather-icon
              icon="Edit2Icon"
              class="edit-icon flex-shrink-0"
              @click="editOption(index)"
            />
          </div>
        </div>
      </div>

      <div
        v-if="
          fieldState.type === 'Синглселект' ||
            fieldState.type === 'Мультиселект'
        "
      >
        <div class="distance-add--form">
          <div class="row options-row">
            <div class="col-6">
              <label class="distance_subtitle">Название опции</label>
              <b-form-input
                id="option-name"
                v-model="optionName"
                type="text"
                no-wheel
                placeholder="Название опции"
                class="inline-block"
              />
            </div>
            <div class="col-4">
              <label class="distance_subtitle">Цена</label>
              <price-input-wrapper
                :value="optionPrice"
                @input="value => (optionPrice = value)"
              />
            </div>
            <b-button
              title="Добавить"
              variant="primary"
              class="add-button btn-icon inline-block distance-add--button"
              size="sm"
              :disabled="!optionName"
              @click="addOption"
            >
              <feather-icon icon="PlusIcon" />
            </b-button>
          </div>
          <div class="row mt-1">
            <div class="col-12">
              <label class="distance_subtitle">Описание опции</label>
              <b-form-input
                v-model="optionDescription"
                type="text"
                placeholder="Описание опции"
              />
            </div>
          </div>
          <div class="row mt-1">
            <div class="col-12">
              <label class="distance_subtitle">Изображения опции</label>
              <file-multi-uploader
                :key="newOptionKey"
                :file-list="newOptionFileList"
                @input="ids => (optionImageIds = ids)"
                @files-change="files => (optionImages = files)"
              />
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="fieldState.type === 'Чекбокс'"
        :key="'field.price?' + fieldIndex"
        class="distance-add--form"
      >
        <b-form-checkbox
          v-model="fieldState.priceBoolean"
          default="false"
          class="distance-add--checkbox"
          @input="resetPrice"
        ><span class="distance_subtitle">Цена?</span>
        </b-form-checkbox>

        <div
          v-if="fieldState.priceBoolean === true || fieldState.price"
          class="distance-add--form"
        >
          <span class="distance_subtitle">Цена</span>
          <price-input-wrapper
            :value="fieldState.price"
            @input="value => (fieldState.price = value)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BFormInput,
  BFormCheckbox,
  BButton,
  BFormCheckboxGroup,
  BTooltip,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { v4 as uuidv4 } from 'uuid'
import flatPickr from 'vue-flatpickr-component'
import { Russian } from 'flatpickr/dist/l10n/ru'
import crud_module from '../../../libs/crud_module'
import PriceInputWrapper from '@/global-components/PriceInputWrapper.vue'
import FileMultiUploader from '@/global-components/FileMultiUploader.vue'

const productsModule = crud_module({
  entity_name: 'product',
})

export default {
  name: 'AdditionalField',
  components: {
    BFormInput,
    BFormCheckbox,
    BButton,
    BFormCheckboxGroup,
    BTooltip,
    vSelect,
    PriceInputWrapper,
    FileMultiUploader,
    flatPickr,
  },
  props: ['field', 'fieldIndex', 'stage', 'distance', 'isRestricted'],
  data() {
    return {
      fieldState: this.additionalField,
      dateConfig: {
        allowInput: true,
        altFormat: 'd.m.Y H:i',
        altInput: true,
        enableTime: true,
        dateFormat: 'Z',
        locale: Russian,
      },
      fieldType: [
        {
          name: 'STRING',
          value: 'Текст',
        },
        {
          name: 'NUMBER',
          value: 'Число',
        },
        {
          name: 'BOOLEAN',
          value: 'Чекбокс',
        },
        {
          name: 'DATE',
          value: 'Дата',
        },
        {
          name: 'MULTI_SELECT',
          value: 'Мультиселект',
        },
        {
          name: 'SELECT',
          value: 'Синглселект',
        },
        {
          name: 'TIME',
          value: 'Время',
        },
      ],
      products: [],
      debounce: null,
      options: [],
      showSelect: null,
      optionName: '',
      optionPrice: null,
      optionDescription: '',
      optionImageIds: [],
      optionImages: [],
      newOptionFileList: [],
      newOptionKey: 0,
      editedOptionIndex: null,
      editedOption: {
        title: '',
        price: null,
        description: '',
        image_ids: [],
        images: [],
      },
    }
  },
  watch: {
    field: {
      handler() {
        this.fieldState = this.field
      },
      immediate: true,
    },
    fieldState: {
      handler(newValue, prev) {
        this.$emit('update-field', this.fieldState, this.fieldIndex, this.stage)
      },
      deep: true,
    },
  },
  async created() {
    if (!this.fieldState.images) {
      this.$set(this.fieldState, 'images', [])
    }
    if (!this.fieldState.image_ids) {
      this.$set(this.fieldState, 'image_ids', [])
    }
    if (this.fieldState.date_from === void 0) {
      this.$set(this.fieldState, 'date_from', null)
    }
    if (this.fieldState.date_to === void 0) {
      this.$set(this.fieldState, 'date_to', null)
    }
    if (this.fieldState.price) {
      this.fieldState.priceBoolean = true
    }
    if (this.stage) {
      this.options = this.stage.additional_fields.filter(
        e => e.uuid !== this.fieldState.uuid
          && (e.type === 'Чекбокс' || e.type === 'Синглселект'),
      )
    } else {
      this.options = this.distance.additional_fields.filter(
        e => e.uuid !== this.fieldState.uuid
          && (e.type === 'Чекбокс' || e.type === 'Синглселект'),
      )
    }
    if (this.fieldState.parent_uuid) {
      if (this.stage) {
        this.fieldState.parent = this.stage.additional_fields.filter(
          field => field.uuid === this.fieldState.parent_uuid,
        )[0]
      } else if (this.distance) {
        this.fieldState.parent = this.distance.additional_fields.filter(
          field => field.uuid === this.fieldState.parent_uuid,
        )[0]
      }
    }
    // console.log('Field state', this.fieldState)
  },
  methods: {
    deleteField() {
      this.$emit('delete-field', this.fieldIndex, this.stage)
    },
    resetPrice() {
      this.fieldState.price = null
    },
    debounceSearch(search, loading, fn) {
      clearTimeout(this.debounce)
      if (search.length > 0) {
        this.debounce = setTimeout(fn, 600)
      }
    },
    removeSingleOption(index) {
      this.fieldState.options.splice(index, 1)
    },
    editOption(index) {
      this.editedOptionIndex = index
      const opt = this.fieldState.options[index]
      this.editedOption = {
        ...opt,
        price: opt.price || null,
        description: opt.description || '',
        image_ids: opt.image_ids ? [...opt.image_ids] : [],
        images: opt.images ? [...opt.images] : [],
      }
    },
    saveEditedOption() {
      this.$set(this.fieldState.options, this.editedOptionIndex, {
        ...this.editedOption,
        price: this.editedOption.price || null,
        description: this.editedOption.description || null,
        image_ids: this.editedOption.image_ids,
        images: this.editedOption.images,
      })
      this.editedOptionIndex = null
    },
    addOption() {
      if (!this.fieldState.options) {
        this.fieldState.options = []
      }
      this.fieldState.options.push({
        uuid: uuidv4(),
        title: this.optionName,
        price: this.optionPrice || null,
        description: this.optionDescription || null,
        image_ids: [...this.optionImageIds],
        images: [...this.optionImages],
      })
      this.optionName = ''
      this.optionPrice = null
      this.optionDescription = ''
      this.optionImageIds = []
      this.optionImages = []
      this.newOptionKey += 1
    },
    addMultipleOption(option) {
      if (!this.fieldState.options) {
        this.fieldState.options = []
      }
      this.fieldState.options = option.map(item => ({
        title: item,
        price: null,
      }))
    },
    getProducts(search, loading) {
      this.debounceSearch(search, loading, async () => {
        try {
          await productsModule
            .fetchItems({
              search,
              limit: 10,
            })
            .then(result => {
              this.products = result.data.rows
              loading(false)
            })
        } catch (e) {
          console.error(e)
        }
      })
    },
    changeParent(e) {
      if (this.stage) {
        const selected = this.stage.additional_fields.filter(
          field => field.uuid === e,
        )[0]
        this.fieldState.parent = selected
        if (selected?.type === 'Чекбокс') {
          this.fieldState.expected_answer = 'true'
        }
      } else {
        const selected = this.distance.additional_fields.filter(
          field => field.uuid === e,
        )[0]
        this.fieldState.parent = selected
        if (selected?.type === 'Чекбокс') {
          this.fieldState.expected_answer = 'true'
        }
      }
    },
  },
}
</script>

<style lang="scss">
.added-option {
  display: flex;
  align-items: flex-start;
  gap: 5px;
  margin-bottom: 8px;

  p {
    margin-bottom: 0;
  }
}

.option-content {
  display: flex;
  align-items: center;
  gap: 5px;
  flex: 1;

  .edit-icon {
    cursor: pointer;

    &:hover {
      color: #7367f0;
    }
  }

  &--editing {
    align-items: flex-start;
    gap: 8px;
  }
}

.option-edit-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.option-info {
  flex: 1;

  p {
    margin-bottom: 0;
  }
}

.option-description {
  font-size: 0.75rem;
  color: #6e6b7b;
  margin-top: 2px;
}

.options-row {
  display: flex;
  align-items: center;

  .add-button {
    margin-left: 14px;
    margin-top: 23px;
    width: 30px;
    height: 30px;
  }
}

.distance-add--form {
  margin-top: 0.5rem;
}

.mt-50 {
  margin-top: 0.5rem;
}

.distance-add--hint {
  margin: 0.25rem 0 0.5rem;
  color: #6e6b7b;
  font-size: 0.75rem;
  line-height: 1.4;
}

.distance_subtitle {
  display: block;
  font-size: 0.75rem;
  margin-bottom: unset;
}

.distance-add--checkbox {
  display: flex;
  align-items: center;
  justify-content: start;
}

.distance_label {
  display: block;
  font-size: 0.75rem;
  margin-bottom: unset;
  cursor: pointer;
}

.distance-add-options {
  margin-top: 0.5rem;

  p {
    margin-bottom: 0;
  }
}

.distance-add--button {
  /* todo избавиться от !important */
  margin-top: 14px !important;
}
</style>
